// REACT IMPORTS
import { useEffect, FC } from "react";
import {
  formatPhoneNumberIntl,
  formatPhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
// COMPONENT IMPORTS

import { Buffer } from "buffer";
import timezoneList from "utils/timezones.json";
import { formatHtmlContent } from "utils/htmlContent";

import {
  accountPermissionGet,
  clearAuthSlice,
  financialSettingsGet,
} from "store/slices/authSlice";
import {
  clearCommonSlice,
  updateCheckAmountState,
  updateShowOverlayState,
} from "store/slices/commonSlice";
import {
  clearDashboardSlice,
  portalSettingsGet,
} from "store/slices/dashboardSlice";
import { clearDocumentSlice } from "store/slices/documentsSlice";
import {
  clearPaymentsSlice,
  generalSettingsGet,
} from "store/slices/paymentsSlice";
import { clearSubscriptionSlice } from "store/slices/subscriptionSlice";
import { clearActionSlice } from "store/slices/actionSlice";
import { clearPaymentMethodSlice } from "store/slices/paymentMethodSlice";
import { clearAiSlice } from "store/slices/aiSlice";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { resolvePaymentAmountLimits } from "utils/paymentAmountValidation";

// ICONS IMPORTS
import { BankIcon } from "icons/dashboard/bankIcon";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import { PaypalIcon } from "icons/dashboard/paypal";
import { WireIcon } from "icons/dashboard/wireIcon";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { VisaIcon } from "icons/payments/VisaIcon";
import { DollarCircleIcon } from "icons/payments/DollarCircleIcon";
import { DollarCheckIcon } from "icons/payments/DollarCheckIcon";
import { MobileIcon } from "icons/auth/Mobile";
import { WorkIcon } from "icons/auth/Work";
import { OtherIcon } from "icons/auth/Other";
import { HomeIcon } from "icons/auth/Home";
import moment from "moment";
import { GoogleIcon } from "icons/dashboard/google";
import JCB2 from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import Discover from "icons/payments/Discover";
import dinersClub from "icons/payments/dinersClub.svg";
import { State } from "country-state-city";
import { formatCardExpiry, formatDateTimeZone } from "./DateFormat";
import { AppleIcon } from "icons/dashboard/apple";
import UnionPay from "icons/payments/UnionPay";
import { ENCDE_SECRET, ENCDE_BASE } from "store/api/constApi";
import AmazonPay from "icons/dashboard/AmazonPay";
import { BankIconSm } from "icons/payments/BankIconSm";
import { BankIcon as BankIconBig } from "icons/payments/BankIcon";
import { DataPaymentValue, valuesProps } from "types/paymentMethods";
import { countryAddressList } from "./countryAddressList";
import { OverPaymentProtectionEnum, SettingsValue } from "types/payments";
import { CardIcon } from "icons/payments/Card";
import AmazonPayIcon from "icons/payments/AmazonPayIcon";
import WalletIcon from "icons/payments/WalletIcon";
import { ApplePayIcon } from "icons/payments/ApplePayIcon";
import { GooglePayIcon } from "icons/payments/GooglePayIcon";
import InvoiceIcon from "icons/header/InvoiceIcon";
import { CardType, PhoneInput } from "types/common";
import { countryCodes } from "pages/profile/countrycode";
import {
  PlanFrequency,
  ProductSubscriptionsEntity,
} from "types/subscriptionAndPlans";
import { CountryCode } from "types/UserProfile";
import { countryToAlpha3 } from "country-to-iso";
import { validateCardExpiry } from "./cardExpiration";
import CustomTooltip from "components/ui-component/CustomTooltip";

export { isCardExpiredSoon, isCreditCardExpired } from "./cardExpiration";

export const useOutsideClick = (ref: any, callback: any) => {
  const handleClick = (e: any) => {
    if (
      ref.current &&
      ref.current.contains &&
      !ref.current.contains(e.target)
    ) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref]);
};

export const shortToCardName = (name: string): string | null => {
  switch (name.toUpperCase()) {
    case "CC":
      return "Card";
    case "WTO":
      return "Wire Transfer";
    case "PP":
      return "PayPal";
    case "ACH":
      return "Bank";
    case "CAPP":
      return "Cash App Pay";
    case "AMZP":
      return "Amazon Pay";
    case "AP":
      return "Apple Pay";
    case "GP":
      return "Google Pay";
    default:
      return "Card";
  }
};

export const getMethodCode = (method: string) => {
  switch (method) {
    case "Card":
      return "CC";
    case "Wire":
      return "WTO";
    case "PayPal":
      return "PP";
    case "Bank":
      return "ACH";
    case "Cash App Pay":
      return "CAPP";
    case "Amazon Pay":
      return "AMZP";
    case "Apple Pay":
      return "AP";
    case "Google Pay":
      return "GP";
    default:
      return "Card";
  }
};

export const getMethod = (
  isAddNewCard: boolean,
  dataPayment: any,
  selectedMethod: string
) => {
  if (isAddNewCard) {
    let method = "CC";
    if (
      dataPayment?.selectedBank &&
      Object.keys(dataPayment?.selectedBank).length > 0
    ) {
      method = "ACH";
    } else if (
      dataPayment?.selectedWallet &&
      Object.keys(dataPayment?.selectedWallet).length > 0
    ) {
      method =
        getMethodCode(
          shortToCardName(dataPayment?.selectedWallet?.payment_method) || ""
        ) || "";
    }
    return method;
  } else {
    return getMethodCode(selectedMethod);
  }
};

export const getInitialsLatters = (value1: any, value2: any) => {
  const first = value1?.charAt(0)?.toUpperCase();
  const second = value2?.charAt(0)?.toUpperCase();
  const combine = first?.toUpperCase() + second?.toUpperCase();

  if (value1?.length > 0 && value2?.length > 0) {
    return combine;
  } else if (value1?.length > 0) {
    return first;
  } else if (value2?.length > 0) {
    return second;
  }
};

const detectCardType = (card: any) => {
  switch (card?.toLowerCase()) {
    case "mc":
    case "mastercard":
      return "Mastercard";
    case "ds":
    case "discover":
      return "Discover";
    case "amex":
    case "american expres":
      return "American Express";
    case "visa":
      return "Visa";
    case "jcb":
      return "JCB";
    case "diners":
    case "dinersclub":
    case "diners club":
      return "Diners Club";
    case "unionpay":
      return "UnionPay";
    default:
      return "";
  }
};

export const cardToShortname = (name: string = "") => {
  switch (name?.toLowerCase()) {
    case "card":
      return "CC";
    case "wire transfer":
      return "WTO";
    case "paypal":
      return "PP";
    case "bank":
      return "ACH";
    case "cash app pay":
      return "CAPP";
    case "google pay":
      return "GP";
    case "apple pay":
      return "AP";
    case "wire":
      return "WTO";
    case "amazon pay":
      return "AMZP";
    case "cash":
      return "CASH";
    case "check":
      return "CHECK";
    case "invoice":
      return "INVOICE";
    default:
      return "";
  }
};

export const formatNumber = (
  phone: string,
  code: string,
  isSupport: boolean = false
) => {
  try {
    let formatted = "";
    const e164Phone: string = `+${code}${phone}`;
    if (!isSupport) {
      formatted = formatPhoneNumberIntl(e164Phone as any);
    } else {
      formatted = `+${code} ` + formatPhoneNumber(e164Phone as any);
    }
    return formatted;
  } catch (error) {
    console.error("Error formatting phone number:", error);
    return `+${code} ${phone}`;
  }
};

export const getStateName = (state: string, country: string) => {
  try {
    const states = State.getStatesOfCountry(country || "US");

    const stateCode = states.find(
      (s) =>
        s.name.toLowerCase() === state.toLowerCase() ||
        s.isoCode.toLowerCase() === state.toLowerCase()
    );

    if (stateCode?.name) {
      return stateCode.name;
    }
  } catch (error) {
    console.log("error ======", error);
  }

  return state;
};

export const formatAddress = (
  state: string,
  country: string,
  city: string,
  address1: string,
  address2: string,
  postal_code: string
) => {
  try {
    let regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    const states = State.getStatesOfCountry(country || "US");
    const stateCode = states.find(
      (s) =>
        s.name.toLowerCase() === state.toLowerCase() ||
        s.isoCode.toLowerCase() === state.toLowerCase()
    );

    let countryName = country?.length > 0 ? `${regionNames.of(country)}, ` : "";
    let formattedCity =
      city?.length > 0 ? <span className="capitalize">{city},</span> : <></>;
    let formattedState =
      stateCode?.name && stateCode.isoCode ? (
        <span className="capitalize">
          {stateCode?.name + ` (${stateCode?.isoCode})`}
        </span>
      ) : state?.length > 0 ? (
        <span className="capitalize">{state + ` (${state?.slice(0, 2)})`}</span>
      ) : (
        <></>
      );
    let formattedAddress1 =
      address1?.length > 0 ? (
        <span className="capitalize">{address1},</span>
      ) : (
        <></>
      );
    let formattedAddress2 =
      address2?.length > 0 ? (
        <span className="capitalize">{address2},</span>
      ) : (
        <></>
      );
    let zip = postal_code?.length > 0 ? `${postal_code},` : "";
    return (
      <>
        {countryName}
        {formattedAddress1} {formattedAddress2} {zip} {formattedCity}{" "}
        {formattedState}
      </>
    );
  } catch (error) {
    console.error(" format address error =====", error);
  }
};

export const validateEmail = (email: string): boolean => {
  const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const getPhoneIcon = (phone_type: string) => {
  switch (phone_type) {
    case "mobile":
      return <MobileIcon />;
    case "work":
      return <WorkIcon />;
    case "home":
      return <HomeIcon />;
    default:
      return <OtherIcon />;
  }
};

export const amountToFormat = (amount: number) => {
  let coverted;
  if (amount >= 1000) {
    coverted = (amount / 1000)?.toFixed(1) + "k";
  } else {
    coverted = amount?.toFixed(2);
  }
  return coverted ?? 0.0;
};

// Keeps the minus sign in front of the currency symbol (-$226.33) rather than
// between it and the number ($-226.33), which is what you get from naively
// prefixing "$" to a negative amount.
export const amountToCurrency = (amount: number) => {
  const value = amount ?? 0;
  return `${value < 0 ? "-" : ""}$${amountToFormat(Math.abs(value))}`;
};

const calculateRemainingTime = (targetDate: any) => {
  const now: any = new Date();
  const difference = targetDate - now;

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
};

const imgUrl = (card_type: any): React.ReactNode => {
  const cardType = card_type;
  switch (cardType?.toLowerCase()) {
    case "visa":
      return <VisaIcon />;
    case "amex":
    case "american express":
    case "americanexpress":
    case "american expres":
      return <img src={amex} alt="AMEX" className="object-contain" />;
    case "mc":
    case "master":
    case "mastercard":
      return <MastercardIcon />;
    case "discover":
    case "ds":
      return <Discover />;
    case "jcb":
      return <img src={JCB2} alt="JCB" className="object-contain" />;
    case "diners":
    case "dinersclub":
    case "diners club":
    case "dc":
      return <img src={dinersClub} alt="DC" />;
    case "unionpay":
      return <UnionPay />;
    default:
      return <CardIcon />;
  }
};

export const getPaymentMethod = ({
  card_type = "",
  method = "",
  digits,
  account_number,
  className,
  textClassName,
  isHidden = false,
  payment_collection_type,
  ivr_type,
  bank_name = "",
  rowId = "",
}: {
  card_type?: string | null;
  method?: string;
  digits?: string;
  account_number?: string;
  className?: string;
  textClassName?: string;
  isHidden?: boolean;
  payment_collection_type?: string;
  ivr_type?: string;
  bank_name?: string;
  rowId?: any;
}) => {
  if (payment_collection_type === "INVOICE") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <InvoiceIcon color="var(--color-txt-secondary)" />
        </span>
        <p
          className={`text-sm text-txtSecondary  leading-normal ${textClassName}`}
        >
          Invoice
        </p>
      </div>
    );
  } else if (method === "CC" && ivr_type === "MANUALPAY") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <CardIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          Card
        </p>
      </div>
    );
  } else if (method === "CC") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span>{imgUrl(card_type)}</span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          {digits ? (isHidden ? `****${digits}` : digits) : "Card"}
        </p>
      </div>
    );
  } else if (method === "ACH") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        {bank_name ? (
          <span className="flex shrink-0 items-center leading-none">
            <CustomTooltip
              className="flex items-center leading-none"
              maxwidth="300px"
              id={`paymentTab-custom-${rowId}`}
              place="top"
              content={`<div class="font-normal text-xs leading-[18px]" > ${bank_name}</div>`}
            >
              {getBankLogo({
                bankName: bank_name,
                type: "small",
                customCss: "!h-[18px]"
              }) || <BankIcon />}
            </CustomTooltip>
          </span>
        ) : (
          <span className="flex shrink-0 items-center leading-none">
            <BankIcon />
          </span>
        )}
        <p
          className={`m-0 flex items-center text-sm text-txtSecondary leading-[18px] ${textClassName}`}
        >
          {isHidden
            ? `Bank ${account_number ? "****" + account_number?.slice(-4) : ""}`
            : `Bank ${account_number ? account_number?.slice(-4) : ""} `}
        </p>
      </div>
    );
  } else if (method === "PP") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <PaypalIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          PayPal
        </p>
      </div>
    );
  } else if (method === "WTO") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span>
          <WireIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          Wire
        </p>
      </div>
    );
  } else if (method === "WT") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span>
          <WireIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          Wire
        </p>
      </div>
    );
  } else if (method === "CAPP") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <CashAppIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary  leading-normal ${textClassName}`}
        >
          Cash App
        </p>
      </div>
    );
  } else if (method === "CASH") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <DollarCircleIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal ${textClassName}`}
        >
          Cash
        </p>
      </div>
    );
  } else if (method === "CHECK") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <DollarCheckIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary leading-normal  ${textClassName}`}
        >
          Check
        </p>
      </div>
    );
  } else if (method === "GP") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <GoogleIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary  leading-normal ${textClassName}`}
        >
          Google Pay
        </p>
      </div>
    );
  } else if (method === "AP") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <AppleIcon />
        </span>
        <p
          className={`text-sm text-txtSecondary  leading-normal ${textClassName}`}
        >
          Apple Pay
        </p>
      </div>
    );
  } else if (method === "AMZP") {
    return (
      <div
        className={`w-full flex items-center gap-[5px] ${
          className ? className : ""
        }`}
      >
        <span className="">
          <AmazonPay />
        </span>
        <p
          className={`text-sm text-txtSecondary  leading-normal ${textClassName}`}
        >
          Amazon Pay
        </p>
      </div>
    );
  } else {
    return "--";
  }
};

export const formatCurrency = (value: any) => {
  const numericValue = Number(String(value).replace(/,/g, ""));
  const threshold = 1e-10;
  if (
    isNaN(numericValue) ||
    Math.abs(numericValue) < threshold ||
    value === "" ||
    value === null ||
    value === undefined ||
    numericValue === 0
  ) {
    value = 0;
    return "$0.00";
  } else {
    value = numericValue;
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
};

const frequencyMap: Record<string, string> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
  year: "yearly",
};

export const formatFrequency = (
  value: any,
  interval_count: number = 0,
  interval_selector: string = "day",
  shortForm: boolean = false
) => {
  let frequency = shortForm ? frequencyMap[value] : `per ${value}`;
  if (value === "quarter") {
    frequency = " every 3 months";
  } else if (value === "semiannual") {
    frequency = " every 6 months";
  } else if (value === "custom") {
    frequency =
      interval_count > 1
        ? ` every ${interval_count} ${interval_selector}s`
        : ` every ${interval_count} ${interval_selector}`;
  }
  return frequency;
};

export const getProductFrequency = (
  value: string,
  count: number,
  selector: "day" | "week" | "month" | "year"
): PlanFrequency => {
  const frequencies: PlanFrequency[] = [
    {
      name: "Daily",
      value: "day",
      interval_custom_count: 1,
      interval_custom_selector: "day",
      bottomDisplay: "per day",
    },
    {
      name: "Monthly",
      value: "month",
      interval_custom_count: 1,
      interval_custom_selector: "month",
      bottomDisplay: "per month",
    },
    {
      name: "Weekly",
      value: "week",
      interval_custom_count: 1,
      interval_custom_selector: "week",
      bottomDisplay: "per week",
    },
    {
      name: "Yearly",
      value: "year",
      interval_custom_count: 1,
      interval_custom_selector: "year",
      bottomDisplay: "per year",
    },
    {
      name: "Every 3 months",
      value: "quarter",
      interval_custom_count: 3,
      interval_custom_selector: "month",
      bottomDisplay: "every 3 months",
    },
    {
      name: "Every 6 months",
      value: "semiannual",
      interval_custom_count: 6,
      interval_custom_selector: "month",
      bottomDisplay: "every 6 months",
    },
    {
      name: "",
      value: "custom",
      interval_custom_count: 0,
      interval_custom_selector: "day",
      bottomDisplay: "",
    },
  ];

  const match = frequencies.find((item) => item.value === value);
  if (match) {
    if (match.value === "custom") {
      let freq = count > 1 ? `${count} ${selector}s` : `${count} ${selector}`;
      return {
        name: `Every ${freq}`,
        value: `every_${freq}`,
        interval_custom_count: count,
        interval_custom_selector: selector,
        bottomDisplay: `every ${freq}`,
      };
    }
    return match;
  }

  return {
    name: "",
    value: "",
    interval_custom_count: 0,
    interval_custom_selector: "day",
    bottomDisplay: "",
  };
};

export function sortIntervals(items: PlanFrequency[]): PlanFrequency[] {
  const unitOrder: Record<PlanFrequency["interval_custom_selector"], number> = {
    day: 1,
    week: 2,
    month: 3,
    year: 4,
  };

  return [...items].sort((a, b) => {
    const unitDiff =
      unitOrder[a.interval_custom_selector] -
      unitOrder[b.interval_custom_selector];

    if (unitDiff !== 0) return unitDiff;

    return a.interval_custom_count - b.interval_custom_count;
  });
}

export const frequencyName = (
  value: string,
  count: number,
  selector: string
) => {
  const frequencies = [
    { name: "daily", value: "day" },
    { name: "monthly", value: "month" },
    { name: "weekly", value: "week" },
    { name: "yearly", value: "year" },
    { name: "every 3 months", value: "quarter" },
    { name: "every 6 months", value: "semiannual" },
    { name: "every 2 days/weeks/months", value: "custom" },
  ];

  const match = frequencies.find((item) => item.value === value);

  if (match) {
    if (match.value === "custom") {
      return formatFrequency(value, count, selector);
    }
    return match.name;
  }

  return "";
};

export const showAmount = (amount: number): string => {
  if (amount < 0) {
    return `(${formatCurrency(amount)})`;
  } else {
    return formatCurrency(amount);
  }
};

export const nomberOfTransactionList = [...Array(10)].map((_, index) => {
  return {
    name: (index + 1).toString(),
    value: index + 1,
  };
});

export function findObjectByValue(arr: any, value: any) {
  const index = arr.findIndex((item: any) => item.value === value);
  return arr[index];
}

export const numberOfTransactions = (total: any) => {
  const arr = [...Array(total)].map((_, index) => {
    return {
      name: (index + 1).toString(),
      value: index + 1,
    };
  });

  return arr;
};

export const rowHelperText = (rowIndex: number) => {
  let value;
  if (rowIndex === 1) {
    value = "st";
  } else if (rowIndex === 2) {
    value = "nd";
  } else if (rowIndex === 3) {
    value = "rd";
  } else {
    value = "th";
  }
  return value;
};

const isDecimalNumber = (number: number) => {
  let value;
  const numTostring = number.toString();
  if (numTostring.includes(".")) {
    const splitNum = numTostring.split(".");
    // eslint-disable-next-line eqeqeq
    if (splitNum[1] == "00") {
      value = number;
    } else {
      value = number.toFixed(2);
    }
  } else {
    value = number.toFixed(2);
  }
  return value;
};
export const percentageCount = (arr: any) => {
  const count = arr
    .map((itm: any) => Number(itm.amount))
    .reduce((prev: any, next: any) => prev + next);
  return count;
};

export const downloadFileFromURL = (
  url: string,
  filename: string
): Promise<boolean> => {
  return fetch(url)
    .then((response) => {
      if (response.ok) {
        return response.blob().then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
          return true;
        });
      } else {
        return false;
      }
    })
    .catch((error) => {
      console.error("Error downloading file:", error);
      return false;
    });
};

function clearNumber(value = "") {
  return value.replace(/\D+/g, "");
}
export function formatExpirationDate(value: any) {
  const clearValue = clearNumber(value);

  if (clearValue.length >= 3) {
    return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 4)}`;
  }

  return clearValue;
}

export function getCountDownAgreement(
  pp_oe_time: string,
  next_billing: any,
  creation: any,
  timezone: any
) {
  if (!pp_oe_time || !next_billing || !creation) {
    return {
      date: null,
      adjustedDate: "",
      timeDiff: 0,
    };
  }

  let next_billing_date = formatDateTimeZone(next_billing, timezone);
  let creation_date = formatDateTimeZone(creation, timezone);

  const [num, units, s] = pp_oe_time.split("-");
  const currentDate = moment();
  let time_in_seconds = 0;
  if (units?.toUpperCase() === "H") {
    time_in_seconds = parseInt(num) * 60 * 60; // hours
  } else if (units?.toUpperCase() === "D") {
    time_in_seconds = parseInt(num) * 24 * 60 * 60; // days
  } else if (units?.toUpperCase() === "W") {
    time_in_seconds = parseInt(num) * 7 * 24 * 60 * 60; // weeks
  }
  let adjustedDate, initialDate;
  if (s === "send") {
    //creating date + pp time
    initialDate = moment(creation_date)?.clone()?.endOf("day");
    adjustedDate = moment(initialDate).add(time_in_seconds, "seconds");
    let next_billing_new = moment(next_billing_date);
    if (adjustedDate?.isBefore(next_billing_new)) {
      // adjuste date is less than next billing
      let time_diff = Math.abs(currentDate.diff(adjustedDate, "seconds"));
      return {
        date: adjustedDate,
        adjustedDate: moment(adjustedDate)?.format("MMMM D, YYYY"),
        timeDiff: time_diff,
      };
    } else {
      // adjuste date is more or equal to next billing
      adjustedDate = moment(next_billing_new).clone()?.endOf("day");
      let time_diff = Math.abs(currentDate.diff(adjustedDate, "seconds"));
      return {
        date: adjustedDate,
        adjustedDate: moment(adjustedDate)?.format("MMMM D, YYYY"),
        timeDiff: time_diff,
      };
    }
  } else {
    //next billing date - pp time
    initialDate = moment(next_billing_date)?.clone()?.endOf("day");
    adjustedDate = moment(initialDate).subtract(time_in_seconds, "seconds");

    if (adjustedDate?.isBefore(creation_date)) {
      // adjustdate is less than creation_date
      adjustedDate = moment(creation_date)?.clone()?.endOf("day");
      let time_diff = Math.abs(currentDate.diff(adjustedDate, "seconds"));
      return {
        date: adjustedDate,
        adjustedDate: moment(adjustedDate)?.format("MMMM D, YYYY"),
        timeDiff: time_diff,
      };
    } else {
      let time_diff = Math.abs(currentDate.diff(adjustedDate, "seconds"));
      return {
        date: adjustedDate,
        adjustedDate: moment(adjustedDate)?.format("MMMM D, YYYY"),
        timeDiff: time_diff,
      };
    }
  }
}

const downloadPDFFromHTML = (htmlContent: string, id: string) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.width = "210mm"; // A4 width in mm
  document.body.appendChild(tempDiv);

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 295; // A4 height in mm
  const scale = 2;
  html2canvas(tempDiv, {
    scale: scale,
    useCORS: true,
    scrollY: -window.scrollY,
    windowHeight: tempDiv.scrollHeight,
    windowWidth: tempDiv.scrollWidth,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 0;
    let heightLeft = imgHeight;

    while (heightLeft > 0) {
      pdf.addImage(
        imgData,
        "PNG",
        0,
        position,
        imgWidth,
        Math.min(imgHeight, pageHeight)
      );
      heightLeft -= pageHeight;

      if (heightLeft > 0) {
        pdf.addPage();
        position = heightLeft - imgHeight;
      }
    }

    pdf.save(`${id}.pdf`);
    document.body.removeChild(tempDiv);
  });
};

const downloadMultiplePagePdf = async (
  content: string[],
  id: any,
  paymentMarkers?: { [key: string]: string }
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  for (let i = 0; i < content.length; i++) {
    if (i > 0) {
      doc.addPage("a4", "portrait");
    }
    let revisedContent = content[i];
    if (paymentMarkers) {
      revisedContent = replaceMarkers(revisedContent, paymentMarkers);
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = revisedContent;
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.width = "793.70px"; // A4 width in pixels (210mm * 96dpi / 25.4mm)
    tempDiv.style.height = "1122.52px"; // A4 height in pixels (297mm * 96dpi / 25.4mm)
    document.body.appendChild(tempDiv);

    // Render using html2canvas
    const canvas = await html2canvas(tempDiv, {
      useCORS: true,
      allowTaint: true,
      scale: 1,
    });

    // Calculate image height based on aspect ratio
    const pdfWidth = doc.internal.pageSize.getWidth();
    // const pdfHeight = doc.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL("image/png");
    const imgProps = doc.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

    // Add image to PDF
    doc.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);

    // Clean up
    tempDiv.remove();
  }

  doc.save(`${id}.pdf`);
};

export const getCardType = (cardNumber: string): string => {
  const cardPatterns: { [key: string]: RegExp } = {
    VISA: /^4/,
    MC: /^(5[1-5]|2[2-7][0-9]{2})/,
    amex: /^3[47]/,
    discover: /^6(?:011|5[0-9]{2})/,
    dinersClub: /^(30[0-5]|36|38)/,
    bccard: /^65/,
    jcb: /^(356|357|358|359)/,
    unionPay: /^62/,
  };

  for (const cardType in cardPatterns) {
    if (cardPatterns[cardType as keyof typeof cardPatterns].test(cardNumber)) {
      return cardType;
    }
  }

  return "unknown";
};

const expiryDateValidation = validateCardExpiry;

export const maskString = (inputString: string) => {
  if (typeof inputString !== "string") {
    return inputString; // Handle non-string input
  }

  // const length = inputString.length;
  const maskedCharacters = "*".repeat(4);
  const visibleCharacters = inputString.slice(-4);

  return maskedCharacters + visibleCharacters;
};

export const obscureEmail = (email: string) => {
  const parts = email.split("@");
  const username = parts[0];
  const domain = parts[1];
  const obscuredUsername =
    username.slice(0, 2) + "*".repeat(username.length - 2);
  return obscuredUsername + "@" + domain;
};

export const isPasswordValid = (password: string) => {
  const requirements = {
    length: /.{8,}/.test(password),
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /[0-9]/.test(password),
    specialChar: /[!@#$%^&*()_+[\]{};':"\\|,.<>?/~`-]/.test(password),
  };

  return requirements;
};

export const clearSlice = (dispatch: any) => {
  dispatch(clearAuthSlice());
  dispatch(clearCommonSlice());
  dispatch(clearPaymentsSlice());
  dispatch(clearDocumentSlice());
  dispatch(clearDashboardSlice());
  dispatch(clearSubscriptionSlice());
  // Action Center items and saved payment methods are per-customer and are
  // persisted to the browser, so they must be wiped alongside everything else —
  // otherwise the next person to sign in on this device sees the previous
  // customer's payment links, agreements, invoices and cards until the new
  // request returns.
  dispatch(clearActionSlice());
  dispatch(clearPaymentMethodSlice());
  // The assistant transcript quotes this customer's balance and payments, so
  // it has to go with the session — otherwise the next person to sign in on a
  // shared machine can reopen the panel and read it.
  dispatch(clearAiSlice());
};
const expiryDateValidationNew = validateCardExpiry;

export const calculateDiffSeconds = (t1: any, t2: any) => {
  const timestamp1 = new Date(t1).getTime();
  const timestamp2 = new Date(t2).getTime();
  // const timestamp2 = new Date(t2).setHours(23, 59, 59, 999);

  const differenceInMillis = timestamp2 - timestamp1;

  const differenceInSeconds = differenceInMillis / 1000;

  return differenceInSeconds;
};

export const convertSeconds = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / (24 * 3600));
  totalSeconds %= 24 * 3600;

  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  let finDays = days < 0 ? 0 : days;
  let finHours = hours < 0 ? 0 : hours;
  let finMinutes = minutes < 0 ? 0 : minutes;
  let finSeconds = seconds < 0 ? 0 : seconds;

  return {
    days: String(finDays).padStart(2, "0"),
    hours: String(finHours).padStart(2, "0"),
    minutes: String(finMinutes).padStart(2, "0"),
    seconds: String(finSeconds).padStart(2, "0"),
  };
};

export const isExpired = (date: any) => {
  const givenDateTime = new Date(date);
  if (Number.isNaN(givenDateTime.getTime())) {
    return false;
  }

  return moment().isAfter(moment(givenDateTime));
};

export default expiryDateValidation;

export const getPerOptions = () => {
  let arr = [];
  for (let i = 0; i <= 100; i++) {
    arr?.push({ name: `${i}%`, value: i });
  }
  return arr;
};

export const generateArrayUpToValue = (dataArr: any, amt: any) => {
  const resultArray = [];
  let totalPr = 0;
  dataArr?.map((val: any) => (totalPr += val?.amount));
  let maxValue = 100 - totalPr + amt;
  for (let i = 1; i <= maxValue; i++) {
    resultArray.push({
      name: `${i}%`,
      value: i,
    });
  }
  return resultArray;
};

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const formatMinAndMaxAmount = (settings: any, balance: any) => {
  const { min, max } = resolvePaymentAmountLimits(settings, balance);
  return { min, max };
};

export const onGetAccountPermission = async (payload: any, dispatch: any) => {
  try {
    let params = {};
    await dispatch(accountPermissionGet(payload)).unwrap(); // view docs settings
    await dispatch(generalSettingsGet(params)).unwrap(); // amount, system settings
  } catch (error) {
    console.log("onGetAccountPermission error =====", error);
  }
};
export const onGetPortalSettings = async (payload: any, dispatch: any) => {
  try {
    await dispatch(portalSettingsGet(payload)).unwrap(); // portal settings
    await dispatch(financialSettingsGet(payload)).unwrap(); // update subs settings
  } catch (error) {
    console.log("onGetPortalSettings error =====", error);
  }
};

export const shouldBypassLegalNotice = (legalNoticeOption: unknown): boolean =>
  legalNoticeOption === SettingsValue.OFF || legalNoticeOption === "";

export { isColorDark } from "utils/colorContrast";

function generateRandomString(length: number) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomString = "";
  for (let i = 0; i < length; i++) {
    randomString += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return randomString;
}

function base64Encode(str: string) {
  return Buffer.from(str, "utf8")
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64Decode(str: string) {
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) {
    str += "=";
  }
  return Buffer.from(str, "base64").toString("utf8");
}

export const encode = (input: string, prefix: string = "") => {
  let secret = ENCDE_SECRET;
  const base = ENCDE_BASE;
  let encoded = "";

  for (let i = 0; i < input?.length; i++) {
    const char = input[i];
    const index = base.indexOf(char);

    if (index !== -1) {
      const newIndex =
        (index + secret.charCodeAt(i % secret.length) - 65) % base.length;
      encoded += base[newIndex];
    } else {
      encoded += char;
    }
  }

  if (prefix === "ci_") {
    const randomPadding = generateRandomString(10);
    encoded = encoded + randomPadding;
  }

  return `${prefix}${base64Encode(encoded)}`;
};

// Decryption function with padding removal
export const decode = (_encoded: string, isClusterId: boolean = false) => {
  let secret = ENCDE_SECRET;
  const base = ENCDE_BASE;
  if (!secret || !base) {
    return _encoded;
  }
  let encoded = _encoded;
  let decoded = "";
  if (isClusterId) {
    encoded = base64Decode(_encoded.substring(3));
    const originalEncoded = encoded.slice(0, -10);

    for (let i = 0; i < originalEncoded.length; i++) {
      const char = originalEncoded[i];
      const index = base.indexOf(char);

      if (index !== -1) {
        const newIndex =
          (index - secret.charCodeAt(i % secret.length) + 65 + base.length) %
          base.length;
        decoded += base[newIndex];
      } else {
        decoded += char;
      }
    }
  } else {
    let prefixPosition = _encoded.indexOf("_");
    if (prefixPosition !== -1) {
      encoded = _encoded.substring(prefixPosition + 1);
    }

    encoded = base64Decode(encoded);

    for (let i = 0; i < encoded.length; i++) {
      const char = encoded[i];
      const index = base.indexOf(char);
      if (index !== -1) {
        const newIndex =
          (index - secret.charCodeAt(i % secret.length) + 65 + base.length) %
          base.length;
        decoded += base[newIndex];
      } else {
        decoded += char;
      }
    }
  }
  return decoded;
};

function isValidHtmlElement(htmlString: string) {
  const tempDiv = document.createElement("div");

  tempDiv.innerHTML = htmlString;

  return Array.from(tempDiv.childNodes).some(
    (node) => node.nodeType === Node.ELEMENT_NODE
  );
}

export const getGeneralDetailMarkers = ({
  paymentLink = null,
  paymentAgreement = null,
  lastDate = null,
  nextDate = null,
  paymentAmount,
  conFee,
}: {
  paymentLink?: string | null; // url
  paymentAgreement?: string | null; // url
  lastDate?: string | null;
  nextDate?: string | null;
  paymentAmount: string;
  conFee: number;
}): { [key: string]: string } => {
  return {
    "General: Payment Link": paymentLink
      ? `<a href=${paymentLink} target="_blank" class="text-blue hover:!text-btnPrimaryHover"> Payment Link</a>`
      : "",
    "General: Payment Agreement": paymentAgreement
      ? `<a href=${paymentAgreement} target="_blank" class="text-blue hover:!text-btnPrimaryHover"> Payment Agreement</a>`
      : "",
    "General: Last Payment Date": lastDate ?? "",
    "General: Next Payment Date": nextDate ?? "",
    "General: Todays Date": moment().format("MM/DD/YYYY"),
    "General: Payment Amount": formatCurrency(
      parseFloat(paymentAmount) + conFee
    ),
    "General: Convenience Fee": formatCurrency(conFee),
  };
};

export const formatContent = formatHtmlContent;

const normalizeMarkerKey = (key: string) =>
  key.replace(/:/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

export function replaceMarkers(
  text: string,
  markers: { [key: string]: any }
): string {
  if (text) {
    const normalizedMarkers = Object.keys(markers).reduce(
      (acc: { [key: string]: any }, markerKey) => {
        acc[normalizeMarkerKey(markerKey)] = markers[markerKey];
        return acc;
      },
      {}
    );

    // Some stored defaults carry their placeholders as dummy links —
    // <a href="#">Company Name</a> — instead of --A--Company Name--B--
    // markers. Treat a "#" link whose text is a known marker as that marker.
    const withLinkMarkers = text.replace(
      /<a\b[^>]*href\s*=\s*["']#["'][^>]*>\s*([^<]{1,60}?)\s*<\/a>/gi,
      (match, linkText) =>
        Object.prototype.hasOwnProperty.call(
          normalizedMarkers,
          normalizeMarkerKey(linkText)
        )
          ? `--A--${linkText.trim()}--B--`
          : match
    );

    // A marker sitting inside an all-capitals passage ("...BY APPLICABLE
    // LAW, --A--Company Legal Name--B-- PROVIDES THE SERVICE...") should read
    // in capitals too, so the value follows the case of its surroundings.
    // Only the nearby text of the same paragraph is inspected, and it must be
    // unmistakably capitalised: at least 8 letters and no lowercase at all.
    const isInsideCapsPassage = (full: string, start: number, end: number) => {
      const blockBoundary = /<\/?(p|br|li|div|h[1-6]|ol|ul|tr|td)\b[^>]*>/gi;
      const clean = (fragment: string) =>
        fragment
          .replace(/<[^>]+>/g, " ")
          .replace(/&nbsp;/gi, " ")
          .replace(/&quot;/gi, '"')
          .replace(/&amp;/gi, "&");
      let before = full.slice(Math.max(0, start - 80), start);
      let lastBoundaryEnd = -1;
      let boundary: RegExpExecArray | null;
      while ((boundary = blockBoundary.exec(before)) !== null) {
        lastBoundaryEnd = boundary.index + boundary[0].length;
      }
      if (lastBoundaryEnd >= 0) {
        before = before.slice(lastBoundaryEnd);
      }
      blockBoundary.lastIndex = 0;
      let after = full.slice(end, end + 80);
      const afterBoundary = after.match(blockBoundary);
      if (afterBoundary && afterBoundary.index !== undefined) {
        after = after.slice(0, afterBoundary.index);
      }
      const context = clean(before + " " + after);
      const upper = (context.match(/[A-Z]/g) || []).length;
      const lower = (context.match(/[a-z]/g) || []).length;
      return upper >= 8 && lower === 0;
    };

    return withLinkMarkers.replace(
      /--A--(.*?)--B--/g,
      (match, markerKey, offset: number, full: string) => {
        const key = markerKey.trim();
        const markerValue =
          Object.prototype.hasOwnProperty.call(markers, key) ||
          Object.prototype.hasOwnProperty.call(markers, markerKey)
            ? (markers[key] ?? markers[markerKey])
            : normalizedMarkers[normalizeMarkerKey(key)];
        // Some marker values arrive with stray whitespace ("Fintrix LLC "),
        // which doubles the spacing in the sentence around them.
        const value =
          markerValue === null || markerValue === undefined
            ? ""
            : String(markerValue).trim();

        if (value === "") {
          return "";
        }

        const isHtml = /<\/?[a-z][\s\S]*>/i.test(value);

        if (isHtml) {
          if (value.startsWith("<a ")) {
            return value.replace("<a ", '<a target="_blank" ');
          }
          return value;
        }

        // An explicitly capitalised marker (--A--COMPANY LEGAL NAME--B--) or a
        // marker inside a capitalised passage yields a capitalised value.
        const keyIsAllCaps = /[A-Z]/.test(key) && key === key.toUpperCase();
        if (
          keyIsAllCaps ||
          isInsideCapsPassage(full, offset, offset + match.length)
        ) {
          return value.toUpperCase();
        }

        return value;
      }
    );
  } else {
    return "";
  }
}

export function adjustColorForHover(hexColor: string) {
  if (hexColor) {
    const percent = -10; // Darken by 10%

    let r = parseInt(hexColor.slice(1, 3), 16);
    let g = parseInt(hexColor.slice(3, 5), 16);
    let b = parseInt(hexColor.slice(5, 7), 16);

    r = Math.round(r * (1 + percent / 100));
    g = Math.round(g * (1 + percent / 100));
    b = Math.round(b * (1 + percent / 100));

    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    const newHex = `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
    return newHex;
  } else {
    return "#1263c8";
  }
}

export function getDisabledColor(hexColor: string) {
  if (hexColor) {
    hexColor = hexColor.replace("#", "");

    let r = parseInt(hexColor.substring(0, 2), 16);
    let g = parseInt(hexColor.substring(2, 4), 16);
    let b = parseInt(hexColor.substring(4, 6), 16);

    const lightenFactor = 0.7896; // 40% lighter
    r = Math.min(255, Math.floor(r + (255 - r) * lightenFactor));
    g = Math.min(255, Math.floor(g + (255 - g) * lightenFactor));
    b = Math.min(255, Math.floor(b + (255 - b) * lightenFactor));

    const toHex = (c: any) => {
      const hex = c.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } else {
    return `#ced8e8`;
  }
}

export const formatCards = (cardList: any) => {
  let res = cardList
    .filter(
      (itm: any) =>
        itm.payment_method === "CC" && (itm.cc_expiry !== "" || itm.cc_expiry)
    )
    .map((itm: any) => {
      const firstLetter = itm.card_funding_type.charAt(0);
      const firstLetterCap = firstLetter.toUpperCase();
      const remainingLetters = itm.card_funding_type.slice(1);
      const capitalizedWord = firstLetterCap + remainingLetters;
      const isValidDate = expiryDateValidationNew(itm.cc_expiry);
      // const msg = itm.cc_expiry ? cardExpiryMsg(itm.cc_expiry) : "";
      return {
        ...itm,
        formatted: {
          id: itm.id,
          name: `${itm.cc_type ? detectCardType(itm.cc_type) : ""} ****${
            itm.cc_4digit
          }`,
          desc: `${capitalizedWord} ${
            itm.cc_expiry
              ? `| ${isValidDate?.isValid ? "Expires" : "Expired"} ${formatCardExpiry(
                  itm.cc_expiry
                )}`
              : ""
          }`,

          card_Type: itm.cc_type ? detectCardType(itm.cc_type) : null,
          disName: `****${itm.cc_4digit}`,
        },
      };
    });

  return res;
};

export const formatBanks = (bankList: any) => {
  let res = bankList
    .filter((itm: any) => itm.payment_method === "ACH")
    .map((itm: any) => {
      return {
        ...itm,
        formatted: {
          id: itm.id,
          name:
            (itm?.bank_name && itm?.bank_name?.trim() !== ""
              ? itm?.bank_name?.toUpperCase()
              : itm.gateway_name) || itm.gateway_name,
          desc: `Bank account ${
            itm.account_number ? `| ****${itm.account_number.substr(-4)}` : ""
          }`,
        },
      };
    });
  return res;
};

export const formatWallets = (walletList: any) => {
  const WALLET_LABELS: Record<string, string> = {
    CAPP: "Cash App",
    AP: "Apple Pay",
    PP: "PayPal",
    GP: "Google Pay",
    AMZP: "Amazon Pay",
  };

  let res = walletList
    .filter((itm: any) =>
      ["CAPP", "AP", "PP", "GP", "AMZP"].includes(itm.payment_method)
    )
    .map((itm: any) => {
      return {
        ...itm,
        formatted: {
          id: itm.id,
          name: WALLET_LABELS[itm.payment_method] || itm.payment_method,
          desc: itm?.desc || "", // fallback if desc not present
        },
      };
    });

  return res;
};

export const cardShortForm = (brand: string) => {
  if (brand?.toLowerCase()?.includes("visa")) {
    return "visa";
  } else if (brand?.toLowerCase()?.includes("american")) {
    return "amex";
  } else if (brand?.toLowerCase()?.includes("diners")) {
    return "dinersClub";
  } else if (brand?.toLowerCase()?.includes("jcb")) {
    return "jcb";
  } else if (brand?.toLowerCase()?.includes("master")) {
    return "mc";
  } else if (brand?.toLowerCase()?.includes("union")) {
      return "mc";
    } else if (brand?.toLowerCase()?.includes("discover")) {
      return "discover";
  } else {
    return brand;
  }
};

export const getBankName = async (q: string) => {
  if (q === "123123123") {
    return { type: "success", bank_name: "ACHQ Test Bank" };
  }
  try {
    let response = await fetch(
      `https://www.usbanklocations.com/crn.php?q=${q}`
    );

    let result = await response.text();

    // Check for errors
    if (
      result.includes("not a valid bank routing number") ||
      result.includes(
        "we can not find an active bank associated with this routing number"
      )
    ) {
      return {
        type: "error",
        bank_name:
          "<span style='color:#ee2a2b;font-weight:normal;font-size:14px;'>The routing number is invalid. Please try again.</span>",
      };
    }

    // Parse the HTML response
    const parser = new DOMParser();
    const doc = parser.parseFromString(result, "text/html");
    const links = Array.from(doc.getElementsByTagName("a")).map(
      (node) => node.textContent
    );

    let bankName = "";
    if (links.length === 1) {
      const tds = Array.from(doc.getElementsByTagName("td"));
      bankName = tds[1]?.textContent || ""; // Assuming the bank name is in the second <td>
    } else {
      bankName = links[0] || "";
    }

    // Check again with the second API if necessary
    if (!bankName) {
      response = await fetch(
        `https://www.routingnumbers.info/api/data.json?rn=${q}`
      );
      const jsonResult = await response.json();
      bankName = jsonResult.customer_name || "";

      if (bankName.length <= 1) {
        bankName =
          "<span style='color:#ee2a2b;font-weight:normal;font-size:14px;'>The routing number is invalid. Please try again.</span>";
        return { type: "error", bank_name: bankName };
      }
    }

    return { type: "success", bank_name: bankName };
  } catch (error) {
    return {
      type: "error",
      bank_name:
        "<span style='color:#ee2a2b;font-weight:normal;font-size:14px;'>An error occurred. Please try again.</span>",
    };
  }
};

interface BankLogos {
  [key: string]: string;
}

const bankLogos: BankLogos = {
  "wells fargo": "wells_fargo",
  "federal reserve": "federal_reserve",
  "street bank": "state_street",
  "bank of america": "bank_of_am",
  "federal home": "federal_home",
  "bank of new york mellon": "bny_mellon",
  oneunited: "one_united",
  "citizens bank": "citi_bank",
  citibank: "citi_bank",
  fiduciary: "fiduciary",
  cathay: "cathay",
  jpmorgan: "jp_morgam",
  "jp morgan": "jp_morgam",
  chase: "jp_morgam",
  pnc: "pnc_bank",
  "pnc bank": "pnc_bank",
  "fifth third": "fifth_third",
  santander: "santander",
  "capital one": "capital_one",
  hsbc: "hsbc_bank",
  "thebancorp.com": "bancorp",
  truist: "truist_bank",
  key: "key_bank",
  regions: "regions_bank",
  "td bank": "td_bank",
  huntington: "huntington_bank",
  woodforest: "woodforest_bank",
  comerica: "comerica",
  bmo: "bmo_bank",
  deutsche: "deutsche",
  arvest: "arvest",
  barclays: "barclays",
  cibc: "cibc",
  synovus: "synovus",
};

interface GetBankLogoProps {
  bankName: string;
  type: "big" | "small";
  customCss?:string;
}

export const getBankLogo: FC<GetBankLogoProps> = ({
  bankName,
  customCss = "",
  type = "small",
}) => {
  if (bankName?.trim() !== "") {
    const bankKey = Object.keys(bankLogos).find((key) =>
      bankName?.toLowerCase()?.includes(key)
    );
    if (bankKey) {
      const bank = bankLogos[bankKey];
      const logo = `${bank}_${type}.svg`;
      const logoPath = require(`icons/banks/${logo}`);

      return (
        <span
          className={`${
            type === "big"
              ? "w-fit h-[60px] flex justify-center items-center"
              : "w-fit h-[23px] flex justify-center items-center"
          } ${customCss}`}
        >
          <img src={logoPath} alt={bankKey} className={`object-contain ${customCss}`} />
        </span>
      );
    }
  }

  return type === "small" ? <BankIconSm /> : <BankIconBig />;
};

interface GetWalletIconProps {
  walletName?: string;
  size?: "small" | "big";
}

const walletIcons: Record<string, React.ReactNode> = {
  "Cash App": <CashAppIcon height="25" width="25" />,
  PayPal: <PaypalIcon height="25" width="25" />,
  "Apple Pay": <ApplePayIcon height="25" width="25" />,
  "Google Pay": <GooglePayIcon height="25" width="25" />,
  "Amazon Pay": <AmazonPayIcon height="25" width="25" />,
};

export const getWalletIcon: FC<GetWalletIconProps> = ({
  walletName,
  size = "small",
}) => {
  if (walletName?.trim() !== "") {
    // Normalize input to lowercase for matching
    const walletKey = Object.keys(walletIcons).find((key) =>
      walletName?.includes(key)
    );

    if (walletKey) {
      const IconComponent = walletIcons[walletKey];
      return (
        <span
          className={
            size === "big"
              ? "w-[120px] h-[60px] flex justify-center items-center"
              : "w-[35px] h-[23px] flex justify-center items-center"
          }
        >
          {IconComponent}
        </span>
      );
    }
  }

  // Default fallback icon if no match found
  return (
    <span
      className={
        size === "big"
          ? "w-[120px] h-[60px] flex justify-center items-center"
          : "w-[35px] h-[23px] flex justify-center items-center"
      }
    >
      <WalletIcon />
    </span>
  );
};

export const checkAddress = (
  cc_billing_address: "Y" | "N",
  cc_billing_addr_mandatory: "Y" | "N",
  values: valuesProps,
  collectAddress: boolean = false
): boolean => {
  if (cc_billing_address === SettingsValue.YES) {
    if (cc_billing_addr_mandatory === SettingsValue.YES && collectAddress) {
      let data = countryAddressList()[values?.x_country || "US"];

      if (!data) {
        return false;
      }

      const arr = Object.keys(data).map((field) => field);
      let addressValid = false;
      //address 1
      if (arr && arr.includes("Address line 1")) {
        if (values?.x_address?.trim() !== "") {
          addressValid = true;
        } else {
          return false;
        }
      } else {
        addressValid = true;
      }

      // city
      if (arr && (arr.includes("City") || arr.includes("Area"))) {
        if (values?.x_city?.trim() !== "") {
          addressValid = true;
        } else {
          return false;
        }
      } else {
        addressValid = true;
      }

      //state
      if (
        arr &&
        (arr.includes("County") ||
          arr.includes("State") ||
          arr.includes("Department") ||
          arr.includes("District") ||
          arr.includes("Emirate") ||
          arr.includes("Island") ||
          arr.includes("Oblast") ||
          arr.includes("Parish") ||
          arr.includes("Prefecture") ||
          arr.includes("Province"))
      ) {
        if (values?.x_state && values?.x_state?.trim() !== "") {
          addressValid = true;
        } else {
          return false;
        }
      } else {
        addressValid = true;
      }

      return addressValid;
    } else {
      return true;
    }
  } else {
    return true;
  }
};

export const checkZip = (values: valuesProps): boolean => {
  let data = countryAddressList()[values?.x_country || "US"];
  if (data) {
    const arr = Object.keys(data).map((field) => field);
    const obj = countryAddressList()[values?.x_country || "US"];
    if (
      arr &&
      obj?.zipCheck &&
      (arr?.includes("CEDEX") ||
        arr?.includes("ZIP") ||
        arr?.includes("Eircode") ||
        arr?.includes("PIN") ||
        arr?.includes("Postal code"))
    ) {
      if (
        values?.x_zip &&
        values?.x_zip?.trim() !== "" &&
        values.x_zip?.length === (obj?.zipLength || 0)
      ) {
        return true;
      } else {
        return false;
      }
    }
  }
  return true;
};

export const checkPhone = (values: PhoneInput): boolean => {
  let flag = false;
  if (values?.phone && isValidPhoneNumber(values?.phone)) {
    flag = true;
  }

  if (values?.country_code && values?.country_code.trim() !== "") {
    flag = true;
  }
  return flag;
};

export const checkNewPaymentDetails = (data: {
  values: valuesProps;
  cc_billing_address: "Y" | "N";
  cc_billing_addr_mandatory: "Y" | "N";
  selectedMethod: string;
  email: string | undefined;
  isErr: number | undefined;
  errMsg: string | undefined;
  collectAddress?: boolean;
}) => {
  const {
    values,
    cc_billing_address,
    cc_billing_addr_mandatory,
    selectedMethod,
    email = "",
    isErr = -1,
    errMsg,
    collectAddress = true,
  } = data;
  switch (selectedMethod) {
    case "Card":
      //card important details
      let addressField = checkAddress(
        cc_billing_address,
        cc_billing_addr_mandatory,
        values,
        collectAddress
      );
      let validZip = checkZip(values);
      if (
        values?.x_card_num?.trim() !== "" &&
        values?.x_exp_date?.trim() !== "" &&
        values?.x_card_code?.trim() !== "" &&
        values?.choldername?.trim() !== "" &&
        values?.x_country?.trim() !== "" &&
        email?.trim() !== "" &&
        validateEmail(email) &&
        isErr === -1 &&
        validZip &&
        addressField
      ) {
        return false;
      } else {
        return true;
      }
    case "Bank":
      if (
        values?.routing_number?.trim() !== "" &&
        values?.routing_number &&
        !errMsg &&
        values?.routing_number?.length === 9 &&
        values?.account_number?.trim() !== "" &&
        values?.choldername?.trim() !== "" &&
        values?.r_account_number?.trim() !== "" &&
        email?.trim() !== "" &&
        validateEmail(email)
      ) {
        // indicating all fields are there and button can be disabled
        return false;
      } else {
        // indicating all fields are not there and button cannot be disabled
        return true;
      }
    case "PayPal":
    case "Wire":
    case "Cash App Pay":
    case "Amazon Pay":
      // indicating all fields are there and button can be disabled
      return false;
    case "Google Pay":
    case "Apple Pay":
      return true;
    default:
      return true;
  }
};

export const checkOldPaymentDetails = (data: {
  dataPayment: DataPaymentValue;
}) => {
  const { dataPayment } = data;
  if (
    dataPayment?.selectedCard &&
    Object.keys(dataPayment?.selectedCard)?.length > 0
  ) {
    const isExpired =
      dataPayment?.selectedCard?.cc_expiry &&
      !expiryDateValidationNew(dataPayment?.selectedCard?.cc_expiry)?.isValid;

    return isExpired;
  } else if (
    (dataPayment?.selectedBank &&
      Object.keys(dataPayment?.selectedBank)?.length > 0) ||
    (dataPayment?.selectedWallet &&
      Object.keys(dataPayment?.selectedWallet)?.length > 0)
  ) {
    return false;
  } else {
    return true;
  }
};

export function calculateUpsellSavings(
  original: any,
  upsell: any,
  productQuantity: number = 1
) {
  // Base everything on days to avoid bias
  const DAYS_MAP: Record<string, number> = {
    day: 1,
    week: 7,
    month: 30,
    quarter: 30 * 3,
    semiannual: 30 * 6,
    year: 365,
  };

  function getPlanDurationInDays(plan: any) {
    if (plan.schedule === "custom") {
      return (
        plan.interval_custom_count * DAYS_MAP[plan.interval_custom_selector]
      );
    }
    return DAYS_MAP[plan.schedule];
  }

  function pricePerDay(plan: any) {
    const days = getPlanDurationInDays(plan);
    return plan.price / days;
  }

  // Upgrade period = upsell plan duration
  const upgradePeriodDays = getPlanDurationInDays(upsell);

  // Cost if user DOES NOT upgrade
  const originalCostForUpgradePeriod =
    pricePerDay(original) * upgradePeriodDays * productQuantity;

  // Cost if user upgrades
  const upsellCost = upsell.price * productQuantity;

  const savingsAmount = originalCostForUpgradePeriod - upsellCost;

  let frequency = upsell.schedule;
  if (frequency === "custom") {
    frequency =
      upsell.interval_custom_count > 1
        ? ` every ${upsell.interval_custom_count} ${upsell.interval_custom_selector}s`
        : ` every ${upsell.interval_custom_count} ${upsell.interval_custom_selector}`;
  }

  if (savingsAmount <= 0) {
    return false;
  }
  return {
    frequency,
    savingsAmount: Number(savingsAmount.toFixed(2)),
  };
}

export const validateCard = (data: {
  values: valuesProps;
  iconType: string;
  setIsErr: (val: number) => void;
  setCardErrMsg: (val: string) => void;
  setErrMsg?: (val: string) => void;
}) => {
  const {
    values,
    iconType,
    setIsErr,
    setCardErrMsg,
    setErrMsg = () => {},
  } = data;

  const cardNum = values["x_card_num"]
    ? values["x_card_num"]?.replaceAll(" ", "")
    : "";

  const cardLength: Record<CardType, number> = {
    MC: 16,
    VISA: 16,
    amex: 15,
    jcb: 16,
    discover: 16,
    dinersClub: 14,
    unionPay: 16,
  };
  let length = cardLength[(iconType as CardType) || "MC"];
  let codeLength = iconType === "amex" ? 4 : 3;
  if (values["x_card_num"] && cardNum?.length > 0 && cardNum?.length < length) {
    setIsErr(1);
    setCardErrMsg("Your card's number is incomplete");
  } else if (
    values["x_card_code"] &&
    values["x_card_code"]?.length > 0 &&
    values["x_card_code"]?.length !== codeLength
  ) {
    setIsErr(3);
    setCardErrMsg(
      values["x_card_code"]?.length < codeLength
        ? "Your card's security code is incomplete"
        : "Your card's security code is incorrect"
    );
  } else if (values["x_exp_date"]) {
    if (values["x_exp_date"]?.length > 0 && values["x_exp_date"]?.length < 5) {
      setIsErr(2);
      setCardErrMsg("Your card's expiration date is incomplete");
    } else {
      let arr = values["x_exp_date"]?.split("/");
      const year = parseInt(20 + arr[1]);
      const month = parseInt(arr[0]);
      if (month < 1 || month > 12) {
        setIsErr(2);
        setCardErrMsg("Your card's expiration date is invalid");
      } else if (moment(year, "YYYY").isBefore(moment(), "year")) {
        setIsErr(2);
        setCardErrMsg("Your card's expiration date is invalid");
      } else if (
        moment(`${year}-${month}-01`, "YYYY-MM-DD").isBefore(moment(), "month")
      ) {
        setIsErr(2);
        setCardErrMsg("Your card's expiration month is in the past");
      } else {
        setIsErr(-1);
        setCardErrMsg("");
      }
    }
  } else {
    setIsErr(-1);
    setErrMsg("");
  }
};

export const checkBankManualFormOrPlaid = ({
  manual_ach,
  validate_bank_account,
  isManualForm,
}: {
  manual_ach: "Y" | "N";
  validate_bank_account: "Y" | "N";
  isManualForm: boolean;
}) => {
  if (manual_ach === SettingsValue.NO) {
    return true;
  } else {
    if (validate_bank_account === SettingsValue.NO) {
      return false;
    } else {
      return !isManualForm;
    }
  }
};

export const checkOverPaymentAmount = async ({
  amount,
  balance,
  overpayment_protection,
  dispatch,
}: {
  amount: number;
  balance: number;
  overpayment_protection: string;
  dispatch: any;
}) => {
  try {
    const settings =
      overpayment_protection === OverPaymentProtectionEnum.ALERT ||
      overpayment_protection === OverPaymentProtectionEnum.BLOCK;
    if (balance - amount < 0 && settings) {
      await dispatch(updateCheckAmountState(true));
      await dispatch(updateShowOverlayState(true));
      return false;
    } else {
      dispatch(updateCheckAmountState(true));
      return true;
    }
  } catch (error) {
    console.log("error =====", error);
  }
};

export const detectCountry = async (): Promise<string> => {
  const fetchWithTimeout = async (url: string, key: string) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);

    try {
      const res = await fetch(url, { signal: controller.signal });

      if (!res.ok) return "";

      const data = await res.json();
      const code = (data?.[key] || "").toString().toUpperCase();

      return code.length === 2 ? code : "";
    } catch {
      return "";
    } finally {
      clearTimeout(timeout);
    }
  };

  const sources: Array<[string, string]> = [
    ["https://ipapi.co/json/", "country_code"],
    ["https://ipwho.is/", "country_code"],
    ["https://ipinfo.io/json", "country"],
  ];

  for (const [url, key] of sources) {
    const code = await fetchWithTimeout(url, key);
    if (code) return code;
  }

  const locale = new Intl.Locale(navigator.language);
  return locale.region || "US";
};

export const checkDefaultMethodExist = (
  cardList: any[],
  bankList: any[],
  walletList: any[]
): "Y" | "N" => {
  if (
    cardList?.[0]?.default_profile === "Y" ||
    bankList?.[0]?.default_profile === "Y" ||
    walletList?.[0]?.default_profile === "Y"
  ) {
    return "N";
  } else {
    return "Y";
  }
};

export const changePaymentScheduleName = (
  payment_schedule: string,
  productSubscriptions?: (ProductSubscriptionsEntity | null)[] | null,
  recurring_type?: string
) => {
  recurring_type = recurring_type || "schedule";

  switch (payment_schedule?.toLowerCase()) {
    case "daily":
    case "day":
      return "Daily";
    case "biweekly":
    case "biweek":
      return recurring_type === "subscription" ? "Every 2 weeks" : "Bi-weekly";
    case "bimonthly":
    case "bimonth":
      return recurring_type === "subscription"
        ? "Every 2 months"
        : "Bi-monthly";
    case "monthly":
    case "month":
      return "Monthly";
    case "weekly":
    case "week":
      return "Weekly";
    case "yearly":
    case "year":
      return "Yearly";
    case "custom":
      let freq = "Custom";

      if (productSubscriptions && productSubscriptions.length > 0) {
        const filteredProds = productSubscriptions.filter(
          (prod) => prod?.priceDetails?.frequency?.toLowerCase() === "recurring"
        );
        const priceDetails = filteredProds?.[0]?.priceDetails;
        freq = changeScheduleName({
          schedule: priceDetails?.schedule,
          interval_custom_count: priceDetails?.interval_custom_count,
          interval_custom_selector: priceDetails?.interval_custom_selector,
          isUpper: true,
          isSubscription: true,
        });
      }
      return freq;
    case "annually":
    case "annual":
      return "Annually";
    case "quarterly":
    case "quarter":
      return recurring_type === "subscription" ? "Every 3 months" : "Quarterly";
    case "biannually":
    case "biannual":
    case "semi-annually":
    case "semiannually":
    case "semiannual":
      return recurring_type === "subscription"
        ? "Every 6 months"
        : "Semi-Annually";
    default:
      if (!payment_schedule) return payment_schedule;
      const normalized = String(payment_schedule).toLowerCase();
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
};

interface ChangeScheduleNameProp {
  schedule?: string;
  interval_custom_count?: number;
  interval_custom_selector?: string;
  isUpper?: boolean;
  isSubscription?: boolean;
}

export const changeScheduleName = (priceDetails: ChangeScheduleNameProp) => {
  const type = priceDetails?.schedule?.toLowerCase();
  const isUpper = priceDetails?.isUpper ?? false;
  const isSubscription = priceDetails?.isSubscription ?? false;
  if (!type) {
    return "";
  }

  let freq = type;

  switch (type) {
    case "day":
      freq = isSubscription ? "daily" : "day";
      return isUpper ? freq.charAt(0).toUpperCase() + freq.slice(1) : freq;
    case "week":
      freq = isSubscription ? "weekly" : "week";
      return isUpper ? freq.charAt(0).toUpperCase() + freq.slice(1) : freq;
    case "month":
      freq = isSubscription ? "monthly" : "month";
      return isUpper ? freq.charAt(0).toUpperCase() + freq.slice(1) : freq;
    case "year":
      freq = isSubscription ? "yearly" : "year";
      return isUpper ? freq.charAt(0).toUpperCase() + freq.slice(1) : freq;
    case "semiannual":
    case "biannually":
      return isUpper ? "Every 6 months" : "every 6 months";
    case "quarter":
      return isUpper ? "Every 3 months" : "every 3 months";
    case "custom": {
      const count = priceDetails?.interval_custom_count ?? 1;
      const selector = priceDetails?.interval_custom_selector ?? "";
      const text = count > 1 ? `every ${count} ${selector}s` : selector;
      return isUpper ? text.charAt(0).toUpperCase() + text.slice(1) : text;
    }
    default:
      return type;
  }
};

export const maskEmail = (email: string): string => {
  if (!email || !email.includes("@")) {
    return "";
  }

  const [username, domain] = email.split("@");
  const visiblePart = username.slice(0, 3);
  const maskedEmail = `${visiblePart}****@${domain}`;

  return maskedEmail;
};

const downloadPdfFile = (pdfBlob: Blob, fileName: string) => {
  const blobUrl = window.URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
};

export const formatRange = (html: string): any => {
  // Extract inner text from any div
  const match = html.match(/<div[^>]*>(.*?)<\/div>/);
  if (!match) return null;

  let text: string = match[1].trim();

  // Normalize spacing
  text = text.replace(/\s+/g, " ");

  const parts = text.split(" - ");
  if (parts.length !== 2) return null;

  const [start, endPart] = parts;

  // Extract year
  const yearMatch = endPart.match(/(\d{4})$/);
  const year = yearMatch ? yearMatch[1] : "";

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr + (year ? ` ${year}` : ""));

    // ✅ Correct TS-safe check
    if (isNaN(d.getTime())) return dateStr;

    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const startFormatted = formatDate(start);
  const endFormatted = formatDate(endPart);

  return { date1: startFormatted, date2: endFormatted + ", " + year };
};

export const downloadPdfFromApi = async (
  apiCall: () => Promise<any>,
  fileName: string,
  setLoading?: (loading: boolean) => void
) => {
  try {
    setLoading?.(true);
    const pdfBlob = await apiCall();
    downloadPdfFile(pdfBlob, fileName);
    setLoading?.(false);
    return true;
  } catch (error: any) {
    console.log("PDF download error:", error?.message);
    setLoading?.(false);
    return false;
  }
};

export const getCountryShortName = (phoneNumber?: string | number) => {
  try {
    const idx =
      typeof phoneNumber === "string" ? parseInt(phoneNumber) : phoneNumber;
    const parsedPhoneNumber = countryCodes[Number(idx)];
    return parsedPhoneNumber ?? "US";
  } catch (error) {
    console.error("Error parsing phone number:", error);
    return "US";
  }
};

export const getCountry3digitCode = (countryCode: CountryCode) => {
  try {
    const res = countryToAlpha3(countryCode);
    return res || countryCode;
  } catch (error) {
    console.log("getCountry3digitCode error ======", error);
    return countryCode;
  }
};

export const getOperatingSystem = () => {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "macOS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (!os && /Linux/.test(platform)) {
    os = "Linux";
  } else if (/CrOS/.test(userAgent)) {
    os = "Chrome OS";
  } else {
    os = "Other";
  }

  return os;
};

export const getBrowserTimezoneOffset = () => {
  const offsetInHours = -new Date().getTimezoneOffset() / 60;
  const sign = offsetInHours >= 0 ? "+" : "-";
  const absHours = Math.abs(offsetInHours);
  const hours = Number.isInteger(absHours)
    ? absHours.toString().padStart(2, "0")
    : absHours.toString();
  return `${sign}${hours}`;
};

const browserTimezoneCityOverrides: { [key: string]: string } = {
  "Asia/Calcutta": "Kolkata",
  "Asia/Kolkata": "Kolkata",
};

interface TimezoneItem {
  id: number;
  timezone_id: string;
  timezone_name: string;
  timezone_area: string;
}

const getTimezoneCity = (timezone?: string) => {
  if (!timezone) return "";
  if (browserTimezoneCityOverrides[timezone]) {
    return browserTimezoneCityOverrides[timezone];
  }

  const city = timezone.split("/").pop()?.replaceAll("_", " ") || "";
  return city;
};

const getBrowserTimezoneListOffset = () => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60).toString();
  const minutes = (absMinutes % 60).toString().padStart(2, "0");

  return `${sign}${hours}.${minutes}`;
};

const normalizeTimezoneValue = (value?: string) =>
  (value || "").toLowerCase().replace(/\s+/g, " ").trim();

const getTimezoneNameFromList = ({
  city,
  timezoneName,
  offset,
}: {
  city: string;
  timezoneName?: string;
  offset: string;
}) => {
  const timezones = timezoneList as TimezoneItem[];
  const normalizedCity = normalizeTimezoneValue(city);
  const normalizedTimezoneName = normalizeTimezoneValue(timezoneName);
  const expectedArea = normalizeTimezoneValue(
    timezoneName && city ? `${timezoneName} - ${city}` : timezoneName
  );
  const offsetMatches = timezones.filter((item) => item.timezone_id === offset);

  return (
    timezones.find(
      (item) => normalizeTimezoneValue(item.timezone_area) === expectedArea
    ) ||
    offsetMatches.find((item) => {
      const area = normalizeTimezoneValue(item.timezone_area);
      return (
        normalizedTimezoneName &&
        area.startsWith(`${normalizedTimezoneName} - `) &&
        normalizedCity &&
        area.includes(normalizedCity)
      );
    }) ||
    offsetMatches.find((item) => {
      const area = normalizeTimezoneValue(item.timezone_area);
      return normalizedCity && area.includes(normalizedCity);
    }) ||
    offsetMatches.find(
      (item) =>
        normalizeTimezoneValue(item.timezone_area) === normalizedTimezoneName
    ) ||
    offsetMatches[0]
  )?.timezone_area;
};

export const getBrowserTimezoneName = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "longGeneric",
    });
    const timezoneName = formatter
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    const city = getTimezoneCity(timezone);
    const offset = getBrowserTimezoneListOffset();
    const timezoneFromList = getTimezoneNameFromList({
      city,
      timezoneName,
      offset,
    });

    return (
      timezoneFromList || timezoneName || timezone || getBrowserTimezoneOffset()
    );
  } catch (error) {
    return getBrowserTimezoneOffset();
  }
};

/**
 * Normalises payment description wording for display.
 * Older records were written with mixed casing (e.g. "Online payment"), so the
 * list would otherwise show the same thing two different ways.
 */
export const formatPaymentDescription = (
  description?: string | null
): string | null => {
  if (!description) return description ?? null;
  const normalised = description.trim().toLowerCase();
  if (normalised === "online payment") return "Online Payment";
  if (normalised === "manual payment") return "Manual Payment";
  if (normalised === "custom plan") return "Custom Plan";
  return description;
};

/**
 * Repairs double-encoded UTF-8 text ("mojibake"), e.g. "Offer 5 ðŸ”¥" → "Offer 5 🔥".
 * Some records reach the API with UTF-8 bytes that were decoded as Windows-1252,
 * so emojis render as garbage. Reversing that mapping restores the original text;
 * strings without mojibake markers (or that fail strict UTF-8 decoding) are
 * returned unchanged, so correct text can never be damaged.
 */
let cp1252ReverseMap: Map<string, number> | null = null;
const getCp1252ReverseMap = (): Map<string, number> => {
  if (!cp1252ReverseMap) {
    const bytes = new Uint8Array(256).map((_, i) => i);
    const chars = new TextDecoder("windows-1252").decode(bytes);
    cp1252ReverseMap = new Map();
    for (let i = 0; i < 256; i++) cp1252ReverseMap.set(chars[i], i);
  }
  return cp1252ReverseMap;
};

export const repairUtf8Mojibake = (input: string): string => {
  if (!/[Â-Åâð]/.test(input)) return input;
  try {
    const map = getCp1252ReverseMap();
    const bytes = new Uint8Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const byte = map.get(input[i]);
      if (byte === undefined) return input;
      bytes[i] = byte;
    }
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return input;
  }
};

export const repairMojibakeDeep = <T,>(value: T): T => {
  if (typeof value === "string") {
    return repairUtf8Mojibake(value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => repairMojibakeDeep(item)) as unknown as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        repairMojibakeDeep(val),
      ])
    ) as unknown as T;
  }
  return value;
};

// Failure reasons come straight from the gateway response. Some are written
// for customers ("Your card was declined."), but backend plumbing errors leak
// through the same field ("Missing stp_customerid or payment_method_id for
// ACH PaymentIntent."). The patterns below catch identifier jargon that no
// customer-facing sentence contains; matches fall back to a generic line so
// internals never render in the portal. The raw reason stays available to
// support in the CRM — this only affects what the customer sees.
const INTERNAL_ERROR_PATTERNS: RegExp[] = [
  /\b(?:stp_|cus_|pm_|pi_|seti_|sub_|ch_|src_|tok_)[A-Za-z0-9_]+/,
  /\b[a-z]+(?:_[a-z]+)+\b/, // snake_case identifiers (payment_method_id, …)
  /\b(?:PaymentIntent|SetupIntent|clientSecret)\b/,
  /\bMissing (?:required )?param/i,
  /\bNo such (?:customer|payment|token|source)/i,
  /\b(?:undefined|NaN|\[object Object\]|Traceback|SQLSTATE|Exception)\b/,
];

export const customerFacingPaymentError = (
  reason?: string | null
): string | null => {
  const text = (reason ?? "").trim();
  if (!text) return null;
  if (INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(text))) {
    return "We couldn't process this payment. Please try a different payment method or contact us for help.";
  }
  return text;
};
